import {FC, useEffect} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import Button from 'components/Button';
import DropdownSelect from 'components/Dropdown/DropdownSelect';
import ProfileCreateFooter from 'components/Footers/Basic';
import Checkbox from 'components/Form/Checkbox';
import FormActions from 'components/Form/FormActions';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import InputText from 'components/Form/InputText';
import Radio from 'components/Form/Radio';
import SubmitButton from 'components/Form/SubmitButton';
import HeaderBasic from 'components/Headers/Basic';
import DualPage from 'components/Layout/DualPage';
import Stacked from 'components/Layout/Stacked';
import SectionHeader from 'components/SectionHeader';
import TooltipIcon from 'components/Tooltip/TooltipIcon';
import {useBackend} from 'state/backend';
import {useLocale} from 'state/locale';
import {setUrl} from 'utils/request';
import {onApiFormError} from 'utils/request/result';
import {SignupRemove} from '../assets/svg';
import {
    CompanyDetailsFormFields,
    UrlParameterOptions,
    UserDetailsFormFields,
} from '../types';
import {UserDetailsBackend} from '../UserDetails';
import {CompanyDetailsFormData, usePostCompanyDetails} from './services';

type CompanyDetailsProps = {
    onBack: () => void;
    onUpdate: (
        formData: Partial<CompanyDetailsFormFields & UserDetailsFormFields>,
    ) => void;
    prefillData?: Partial<CompanyDetailsFormFields & UserDetailsFormFields>;
};

const CompanyDetails: FC<CompanyDetailsProps> = ({
    onBack,
    onUpdate,
    prefillData = {},
}) => {
    const {data} = useBackend<UserDetailsBackend>();

    const methods = useForm<CompanyDetailsFormData>({
        defaultValues: {
            agreedToTerms: prefillData.agreedToTerms,
            companySize: prefillData.companySize,
            numberOfEmployees: prefillData.numberOfEmployees,
            partnerName: prefillData.partnerName,
        },
        shouldUnregister: true,
    });
    const {getValues, handleSubmit, watch} = methods;

    watch('companySize');
    const businessType = getValues('companySize');

    useEffect(() => {
        const subscription = watch((value) => {
            onUpdate(value);
        });

        return () => subscription.unsubscribe();
    });

    const {mutateAsync} = usePostCompanyDetails();

    const languageCode = useLocale();

    const {t} = useTranslation('Profile.Create.CompanyDetails');

    const onSubmit = async (formData: CompanyDetailsFormData) => {
        const submittedData: Partial<
            UserDetailsFormFields &
                CompanyDetailsFormFields &
                UrlParameterOptions
        > = {
            agreedToTerms: formData.agreedToTerms,
            companySize: formData.companySize,
            email: prefillData.email,
            languageCode,
            leadSource: data.leadSource,
            nameFirst: prefillData.nameFirst,
            nameLast: prefillData.nameLast,
            numberOfEmployees:
                formData.companySize === 'self_employed'
                    ? '1to4'
                    : formData.numberOfEmployees,
            partnerName: formData.partnerName,
            password: prefillData.password,
            phoneNumber: prefillData.phoneNumber,
            regcode: prefillData.regcode,
            username: prefillData.username,
            utmCampaign: data.utmCampaign,
            utmContent: data.utmContent,
            utmMedium: data.utmMedium,
            utmSource: data.utmSource,
            utmTerm: data.utmTerm,
        };

        mutateAsync(submittedData)
            .then(() => {
                setUrl(window.Urls.profile_setup_wizard_view());
            })
            .catch(onApiFormError(methods));
    };

    return (
        <div className="relative flex min-h-screen flex-col bg-grey-100 ">
            <HeaderBasic className="px-8" />
            <DualPage
                pagination={{
                    current: 2,
                    length: 2,
                }}
                size="6xl"
            >
                <FormProvider {...methods}>
                    <form
                        className="rounded border border-grey-300 bg-white p-6"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <FormHeader
                            className="mt-0"
                            hideHR={true}
                            title={t('pageForm.title')}
                        />
                        <Stacked className="mt-4 w-full">
                            <Radio
                                isHorizontal={true}
                                label={t('form:businessType')}
                                name="companySize"
                                options={[
                                    {
                                        label: (
                                            <div className="flex items-center">
                                                {t('pageForm.businessType.1')}
                                                <TooltipIcon
                                                    aria-label="organization-info"
                                                    className="ml-1 inline-block"
                                                    message={t(
                                                        'pageForm.organizationHelper',
                                                    )}
                                                    size="base"
                                                />
                                            </div>
                                        ),
                                        value: 'organization',
                                    },
                                    {
                                        label: t('pageForm.businessType.2'),
                                        value: 'self_employed',
                                    },
                                ]}
                                required={true}
                            />
                            <InputText
                                label={t('form:companyName')}
                                name="partnerName"
                                required={true}
                            />
                            {businessType === 'organization' && (
                                <DropdownSelect
                                    aria-label="Number of employees"
                                    label={t('form:employees')}
                                    name="numberOfEmployees"
                                    options={[
                                        {
                                            label: t(
                                                'pageForm.employeeOptions.1',
                                            ),
                                            value: '1to4',
                                        },
                                        {
                                            label: t(
                                                'pageForm.employeeOptions.2',
                                            ),
                                            value: '5to20',
                                        },
                                        {
                                            label: t(
                                                'pageForm.employeeOptions.3',
                                            ),
                                            value: '21to50',
                                        },
                                        {
                                            label: t(
                                                'pageForm.employeeOptions.4',
                                            ),
                                            value: '51to100',
                                        },
                                        {
                                            label: t(
                                                'pageForm.employeeOptions.5',
                                            ),
                                            value: '101to300',
                                        },
                                        {
                                            label: t(
                                                'pageForm.employeeOptions.6',
                                            ),
                                            value: '301to1000',
                                        },
                                        {
                                            label: t(
                                                'pageForm.employeeOptions.7',
                                            ),
                                            value: '1001',
                                        },
                                    ]}
                                    required={true}
                                />
                            )}
                            <div>
                                <Checkbox
                                    label={
                                        <Trans
                                            components={[
                                                <a
                                                    key="0"
                                                    href="https://www.makeleaps.jp/利用規約/"
                                                    rel="noreferrer"
                                                    target="_blank"
                                                >
                                                    {' '}
                                                </a>,
                                                <a
                                                    key="1"
                                                    href="https://www.makeleaps.jp/プライバシーポリシー/"
                                                    rel="noreferrer"
                                                    target="_blank"
                                                >
                                                    {' '}
                                                </a>,
                                            ]}
                                            i18nKey="agreeTOS"
                                            ns="form"
                                        />
                                    }
                                    name="agreedToTerms"
                                    rules={{
                                        required: t('pageForm.agreement'),
                                    }}
                                />
                            </div>
                            <FormResult />
                            <FormActions>
                                <Button kind="tertiary" onClick={onBack}>
                                    {t('form:goBack')}
                                </Button>
                                <SubmitButton>
                                    {t('pageForm.createAccount')}
                                </SubmitButton>
                            </FormActions>
                        </Stacked>
                    </form>
                </FormProvider>
                <div className="p-6">
                    <SectionHeader
                        className="mt-0"
                        hideHR={true}
                        title={t('information.content1Title')}
                    />
                    <section className="mt-6 flex">
                        <SignupRemove className="w-40 flex-none" />
                        <div>
                            <p className="m-0">
                                {t('information.content1Content')}
                            </p>
                        </div>
                    </section>
                </div>
            </DualPage>
            <ProfileCreateFooter />
        </div>
    );
};

export default CompanyDetails;
